'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { Button, WorkflowStepper } from '@ds/ui'
import { BannerInfo } from '../../types/BannerInfo';
import { apiFetch, handleApiError } from '../../utils/apiClient';

interface KeyInfo {
  id?: string
  filename?: string
}

interface WorkflowInfo {
  filehash?: string
  redKey?: KeyInfo
  blueKey?: KeyInfo
  redKeyfilename?: string
  blueKeyfilename?: string
}

interface ExportWorkflowProps {
  mode: 'export' | 'create'
  controller: 'Home' | 'KeyPair'
  onBannerUpdate: (bannerInfo: BannerInfo | null) => void
  onComplete?: () => void
}

export default function ExportWorkflow({
  mode,
  controller,
  onBannerUpdate,
  onComplete
}: ExportWorkflowProps) {
  const [activeStep, setActiveStep] = useState(0)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [isDownloadingRed, setIsDownloadingRed] = useState(false)
  const [isDownloadingBlue, setIsDownloadingBlue] = useState(false)
  const [isDownloadingWhole, setIsDownloadingWhole] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isComplete, setIsComplete] = useState(false)
  const [workflowInfo, setWorkflowInfo] = useState<WorkflowInfo>({})

  
  const fetchWorkflowInfo = useCallback(async () => {
    try {
      const endpoint = `Admin/${controller}/${mode === 'export' ? 'GetExportInfo' : 'GetCreateInfo'}`
      const response = await apiFetch(endpoint)
      if (!response.ok)  {
        await handleApiError(response);
      }
      const data = await response.json()
      
      const exportInfo = data.exportInfo;
      setWorkflowInfo(exportInfo)
    } catch (err) {
      console.error('Error fetching workflow info:', err)
      onBannerUpdate({ type: 'danger', message: 'Failed to initialize workflow' });
    }
  }, [controller, mode])

  useEffect(() => {
    fetchWorkflowInfo()
  }, [fetchWorkflowInfo])

  const handleDownload = useCallback(async (fileType: 'file' | 'keyred' | 'keyblue') => {
    try {
      if (fileType === 'keyred') {
        setIsDownloadingRed(true)
      } else if (fileType === 'keyblue') {
        setIsDownloadingBlue(true)
      } else {
        setIsDownloadingFile(true)
      }
      setError(null)

      const formData = new FormData()
      formData.append('fileType', fileType)

      const endpoint = `Admin/${controller}/ExportFile`
      const response = await apiFetch(endpoint, {
        method: 'POST',
        body: formData
      })

      if (response.status === 202 || !response.ok) {
        const data = await response.json();
        const errorMessage = typeof data.error === 'string' ? data.error : 'Unknown error occurred';
        throw new Error(errorMessage);
      }

      const contentDisposition = response.headers.get('Content-Disposition')
      const filenameMatch = contentDisposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const filename = filenameMatch 
        ? filenameMatch[1].replace(/["']/g, '') 
        : `${fileType}.jec`

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      setActiveStep(prevStep => prevStep + 1)
      await fetchWorkflowInfo()
      const fileName = fileType === 'file' ? 'File' : fileType === 'keyred' ? 'Red Key' : 'Blue Key'
      onBannerUpdate({ type: 'success', message: `${fileName} downloaded successfully` });
    } catch (err) {
      console.error(`Error ${mode}ing file:`, err)
      const defaultMessage = `An error occurred while ${mode}ing the file. Please try again.`;
      onBannerUpdate({ 
        type: 'danger', 
        message: err instanceof Error ? err.message : defaultMessage,
        retryAction: undefined
      });
      throw new Error(err instanceof Error ? err.message : defaultMessage);
    } finally {
      setIsDownloadingFile(false)
      setIsDownloadingRed(false)
      setIsDownloadingBlue(false)
    }
  }, [controller, mode, fetchWorkflowInfo])

  const handleComplete = useCallback(async (action: 'done' | 'cancel') => {
    setIsDownloadingWhole(true)
    try {
      const formData = new FormData()
      const tag = controller.replace('React', '')
      formData.append('tag', tag)
      formData.append('button', action === 'cancel' ? 'Cancel' : 'Complete')

      const endpoint = `Admin/${controller}/ExportDone`
      await apiFetch(endpoint, {
        method: 'POST',
        body: formData
      })
      if (action === 'done') {
        onBannerUpdate({ type: 'success', message: `Done` });
      } else {
        onBannerUpdate({ type: 'success', message: `Cancelled`, retryAction: undefined });
      }
    } catch (err) {
      console.error('Error completing workflow:', err)
      onBannerUpdate({ type: 'danger', message: 'Failed to complete workflow: ' + err, retryAction: undefined });
    } finally {
      setIsDownloadingWhole(false)
      setIsComplete(true)
      onComplete?.()
    }
  }, [controller, onComplete])

  if (!workflowInfo) {
    return <div>Loading...</div>
  }

  return (
    <div className="max-w-md mx-auto" style={{ maxWidth: '28.125rem' }}>
      {!isComplete && (
        <>
          {mode === 'export' && (
            <p className="text-sm text-gray-500 mb-4">Exported key files are removed after 24 hours</p>
          )}
          <WorkflowStepper>
            <WorkflowStepper.Item
              text="Download File"
              status={'current'}
              description={workflowInfo.filehash ? `File ID ${workflowInfo.filehash.substring(0, 29)}` : ''}
              callToAction={<Button
                kind="secondary"
                onClick={() => handleDownload('file')}
                disabled={isDownloadingFile }
                text={isDownloadingFile  ? `Downloading...` : 'Download'}
                loading={isDownloadingFile}
              />}
            >
              <span className="ml-2 text-sm text-gray-600">
                {/* File ID {(workflowInfo.filehash ?? '').substring(0, 29)} */}
              </span>
            </WorkflowStepper.Item>


            <WorkflowStepper.Item
              text="Download Red Key"
              status={'current'}
              description={workflowInfo.redKey?.id ? `Key ID ${workflowInfo.redKey?.id?.substring(0, 29)}` : ""}
              callToAction={<Button
                kind="secondary"
                onClick={() => handleDownload('keyred')}
                disabled={isDownloadingRed }
                text={isDownloadingRed  ? `Downloading...` : 'Download'}
                loading={isDownloadingRed}
              />}
            >
              <span className="ml-2 text-sm text-gray-600">
              </span>
            </WorkflowStepper.Item>

            <WorkflowStepper.Item
              text="Download Blue Key"
              status={'current'}
              description={workflowInfo.blueKey?.id ? `Key ID ${workflowInfo.blueKey?.id?.substring(0, 29)}` : ""}
              callToAction={<Button
                kind="secondary"
                onClick={() => handleDownload('keyblue')}
                disabled={isDownloadingBlue }
                text={isDownloadingBlue  ? `Downloading...` : 'Download'}
                loading={isDownloadingBlue}
              />}
            >
              <span className="ml-2 text-sm text-gray-600">
              </span>
            </WorkflowStepper.Item>
          </WorkflowStepper>

          <div className="mt-6 flex justify-end space-x-4">
            <Button
              kind="secondary"
              onClick={() => handleComplete('cancel')}
              text="Cancel"
            />
            <span style={{ marginLeft: '0.75rem' }}></span>
            <Button
              kind="primary"
              onClick={() => handleComplete('done')}
              disabled={ isDownloadingWhole}
              text={'Done'}
              loading={isDownloadingWhole}
            />
          </div>
        </> 
      )}
    </div>
  )
}
